'use client'

import { InfoCircle16 } from '@vinted/monochrome-icons'
import { Icon } from '@vinted/web-ui'
import { KeyboardEvent, useState } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { KeyboardKey, onA11yKeyDown } from '@marketplace-web/vendor-abstractions/web-ui-util'
import { clickEvent } from '@marketplace-web/escrow-pricing/escrow-pricing-data'

import BuyerProtectionModal from '../BuyerProtectionModal'

type Props = {
  itemId: number
  bundleId?: string | null
  isSellerBusiness?: boolean
}

const ItemPricingDetailsIcon = ({ itemId, isSellerBusiness = false, bundleId = null }: Props) => {
  const translate = useTranslate(
    isSellerBusiness ? 'item_pricing_details_modal.pro' : 'item_pricing_details_modal.regular',
  )

  const { track } = useTracking()

  const [isModalVisible, setIsModalVisible] = useState(false)

  const trackIconClick = () => {
    if (bundleId) {
      track(
        clickEvent({
          screen: 'bundle_summary',
          target: 'service_fee_info',
          targetDetails: JSON.stringify({ bundle_id: bundleId }),
        }),
      )
    } else {
      track(
        clickEvent({
          target: 'service_fee_info',
          targetDetails: JSON.stringify({ item_id: itemId }),
        }),
      )
    }
  }

  const handleIconClick = () => {
    setIsModalVisible(true)

    trackIconClick()
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    onA11yKeyDown(event, { keys: [KeyboardKey.Enter, KeyboardKey.Space] }, handleIconClick)
  }

  const renderContent = () => (
    <>
      <div
        className="u-flexbox u-align-self-center u-cursor-pointer"
        data-testid="item-pricing-details-icon"
        role="button"
        tabIndex={0}
        onClick={handleIconClick}
        onKeyDown={handleKeyDown}
      >
        <Icon name={InfoCircle16} color={Icon.Color.GreyscaleLevel2} title={translate('title')} />
      </div>
      <BuyerProtectionModal
        isVisible={isModalVisible}
        onClose={handleModalClose}
        sellerIsBusinessUser={isSellerBusiness}
      />
    </>
  )

  return renderContent()
}

export default ItemPricingDetailsIcon
